import { Component, Input, Output, EventEmitter, OnChanges, OnInit, inject, signal, DestroyRef, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject, catchError, debounceTime, distinctUntilChanged, forkJoin, map, of, switchMap, throwError } from 'rxjs';

import { Fountain } from '../../../models/Fountain';
import {
  GenerateNewSourceBoletaRequest,
  GenerateNewSourceBoletaResponse,
  OpenPublicationMonth,
  SourceService
} from '../../../service/source.service';
import { SourceContact, SourceContactsService } from '../../../service/source-contacts.service';
import { ModalDeleteSource } from '../modal-delete-source/modal-delete-source';
import { SourceArticle } from '../../../features/source-articles/models/source-article.model';
import { SourceArticlesService } from '../../../features/source-articles/services/source-articles.service';
import { getCurrentUserId } from '../../../shared/utils/auth-user.util';

import { GeneralService } from '../../../service/general.service';
import { DepartmentsGT } from '../../../enum/departments.enum';

import { Municipality } from '../../../service/municipality.service';
import { MunicipalityGT } from '../../../enum/municipalities.enum';

import { CodigoPostalService } from '../../../service/codigo-postal.service';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

const DEFAULT_EMAIL = 'nd@nd.com';
const DEFAULT_JORNADAS = {
  horaInicioPrimeraJornada: '08:00',
  horaFinalPrimeraJornada: '12:00',
  horaInicioSegundaJornada: '13:00',
  horaFinalSegundaJornada: '16:00'
};
const PHONE_TEXT_PATTERN = /^[A-Za-z0-9áéíóúÁÉÍÓÚñÑ\s()+\-./#]*$/;
const NIT_TEXT_PATTERN = /^[A-Za-z0-9\s/-]*$/;
const MONTH_LABELS = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre'
];

interface CodigoPostalQuery {
  depId: number;
  munId: number;
  referencia: string;
  preservarActual: boolean;
}

interface CodigoPostalLookupResult {
  lista: any[];
  preservarActual: boolean;
  preserveCurrentState: boolean;
}

interface MobileSelectOption {
  value: string | number;
  label: string;
}

interface OpenPublicationMonthOption {
  key: string;
  month: OpenPublicationMonth;
  label: string;
}

type MobileSelectControlName =
  | 'fkDepartamento'
  | 'fkMunicipio'
  | 'fkUsuario'
  | 'periodo'
  | 'fkPeriodicidadVisita'
  | 'fkDiaVisita'
  | 'requiereAudio'
  | 'requiereImagen'
  | 'fkActividadEmpresa'
  | 'fkTamanoEmpresa'
  | 'estado';

const jornadasCongruentesValidator: ValidatorFn = (
  group: AbstractControl
): ValidationErrors | null => {

  const h1i = group.get('horaInicioPrimeraJornada')?.value;
  const h1f = group.get('horaFinalPrimeraJornada')?.value;
  const h2i = group.get('horaInicioSegundaJornada')?.value;
  const h2f = group.get('horaFinalSegundaJornada')?.value;

  const toMinutes = (t: string) => {
    if (!t || !t.includes(':')) return NaN;
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };

  let errors: ValidationErrors = {};

  // -------- PRIMERA JORNADA --------
  if (h1i && h1f) {
    const i1 = toMinutes(h1i);
    const f1 = toMinutes(h1f);
    if (!isNaN(i1) && !isNaN(f1) && f1 <= i1) {
      errors['primeraJornadaInvalida'] = true;
    }
  }

  // -------- SEGUNDA JORNADA --------
  if (h2i && h2f) {
    const i2 = toMinutes(h2i);
    const f2 = toMinutes(h2f);
    if (!isNaN(i2) && !isNaN(f2) && f2 <= i2) {
      errors['segundaJornadaInvalida'] = true;
    }

    if (h1f) {
      const f1 = toMinutes(h1f);
      if (!isNaN(i2) && !isNaN(f1) && i2 < f1) {
        errors['traslapeJornadas'] = true;
      }
    }
  }

  return Object.keys(errors).length ? errors : null;
};


@Component({
  selector: 'app-modal-edit-source',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ModalDeleteSource],
  templateUrl: './modal-edit-source.html',
  styleUrls: ['./modal-edit-source.scss']
})
export class ModalEditSource implements OnInit, OnChanges {
  private readonly destroyRef = inject(DestroyRef);
  private readonly codigoPostalQuery$ = new Subject<CodigoPostalQuery>();

  @Input() fountain: Fountain | null = null;
  @Output() deleted = new EventEmitter<void>();
  @Output() updated = new EventEmitter<Fountain>();

  geoLoading = signal(false);
  loadingOpenMonths = signal(false);
  generatingBoleta = signal(false);
  saving = signal(false);
  success = signal(false);
  error = signal<string | null>(null);
  boletaMessage = signal<string | null>(null);
  boletaMessageType = signal<'success' | 'danger'>('success');
  openMonthsModalOpen = signal(false);
  openPublicationMonths = signal<OpenPublicationMonth[]>([]);
  selectedOpenPublicationMonthKey = signal<string | null>(null);

  private fb = inject(FormBuilder);
  private sourceService = inject(SourceService);
  private sourceContactsService = inject(SourceContactsService);
  private sourceArticlesService = inject(SourceArticlesService);
  private generalService = inject(GeneralService);
  private municipalityService = inject(Municipality);
  private codigoPostalService = inject(CodigoPostalService);

  constructor() {
    this.setupCodigoPostalLookup();
  }

  // Snapshot EXACTO visible al abrir editar
  private snapshotAntes: any = null;

  usuarios = signal<any[]>([]);
  periodicidades = signal<any[]>([]);
  actividades = signal<any[]>([]);
  tamanios = signal<any[]>([]);
  codigoPostalCatalog = signal<any[]>([]);
  diasVisita = signal<any[]>([]);
  periodoTitulo = signal<string>('Periodo');
  periodoLimite = signal<number>(12);
  readonly mobileSelectOpen = signal(false);
  readonly mobileSelectTitle = signal('');
  readonly mobileSelectControl = signal<MobileSelectControlName | null>(null);
  readonly mobileSelectOptions = signal<MobileSelectOption[]>([]);
  readonly mobileSelectQuery = signal('');
  readonly filteredMobileSelectOptions = computed(() => {
    const query = this.normalizeSearchText(this.mobileSelectQuery());
    const options = this.mobileSelectOptions();

    if (!query) {
      return options;
    }

    return options.filter((option) =>
      this.normalizeSearchText(option.label).includes(query)
    );
  });
  readonly openPublicationMonthOptions = computed<OpenPublicationMonthOption[]>(() =>
    this.openPublicationMonths().map((month) => ({
      key: this.getOpenPublicationMonthKey(month),
      month,
      label: this.getOpenPublicationMonthLabel(month)
    }))
  );

  municipalities = signal<{ id: number; nombre: string }[]>([]);
  codigosPostales = signal<any[]>([]);
  codigoPostalDescripcion = '';
  readonly yesNoOptions: MobileSelectOption[] = [
    { value: 'S', label: 'Sí' },
    { value: 'N', label: 'No' }
  ];
  readonly statusOptions: MobileSelectOption[] = [
    { value: 'A', label: 'Activo' },
    { value: 'P', label: 'Pendiente' },
    { value: 'I', label: 'Inactivo' }
  ];

  departaments = Object.entries(DepartmentsGT)
    .filter(([_, v]) => !isNaN(Number(v)))
    .map(([k, v]) => ({ id: Number(v), nombre: k }));

  selectedFountain: Fountain | null = null;

  editForm = this.fb.nonNullable.group({
    codigoINE: ['', Validators.required],
    nombre: ['', Validators.required],
    razonSocial: [''],
    direccion: [''],

    fkDepartamento: [0],
    fkMunicipio: [0],

    zona: [0],
    fkCodigoPostal: [0],

    gpsLatitud: [0],
    gpsLongitud: [0],
    gpsAltitud: [0],
    gpsFecha: [''],

    telefonos: ['', [Validators.pattern(PHONE_TEXT_PATTERN)]],
    email: ['', Validators.email],

    fkUsuario: [0],
    periodo: [0, Validators.min(1)],
    ordenRuta: [0, Validators.min(0)],
    fkPeriodicidadVisita: [0],
    fkDiaVisita: [0],

    horaInicioPrimeraJornada: [DEFAULT_JORNADAS.horaInicioPrimeraJornada],
    horaFinalPrimeraJornada: [DEFAULT_JORNADAS.horaFinalPrimeraJornada],
    horaInicioSegundaJornada: [DEFAULT_JORNADAS.horaInicioSegundaJornada],
    horaFinalSegundaJornada: [DEFAULT_JORNADAS.horaFinalSegundaJornada],

    requiereAudio: ['N'],
    requiereImagen: ['N'],

    nit: ['', [Validators.pattern(NIT_TEXT_PATTERN)]],
    fkActividadEmpresa: [0],
    fkTamanoEmpresa: [0],
    codigoCIIU: [''],

    comentarios: [''],
    referencia: [''],
    informacionAdicional: [''],

    estado: ['P']
  }, {
    validators: jornadasCongruentesValidator
  });


  ngOnInit(): void {
    this.loadCatalogs();
  }

  private setupCodigoPostalLookup(): void {
    this.codigoPostalQuery$
      .pipe(
        debounceTime(400),
        distinctUntilChanged(
          (previous, current) =>
            previous.depId === current.depId &&
            previous.munId === current.munId &&
            previous.referencia === current.referencia &&
            previous.preservarActual === current.preservarActual
        ),
        switchMap(({ depId, munId, referencia, preservarActual }) =>
          this.requestCodigoPostalLookup(depId, munId, referencia, preservarActual)
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result) => this.applyCodigoPostalLookupResult(result));
  }

  private isTransientCodigoPostalError(error: unknown): boolean {
    return error instanceof HttpErrorResponse && (error.status === 0 || error.status === 502);
  }

  ngOnChanges(): void {
    if (!this.fountain) return;

    this.boletaMessage.set(null);
    this.closeOpenMonthsModal();
    this.loadingOpenMonths.set(false);
    this.generatingBoleta.set(false);
    const formValue = this.mapFountainToForm(this.fountain);
    this.editForm.reset(formValue);
    this.hydrateCodigoPostalCatalogFromCache();
    this.syncCodigoPostalDescripcion();

    // snapshot para auditoría (ANTES)
    this.snapshotAntes = structuredClone(this.editForm.getRawValue());

    // ✅ CARGA DEP/MUNI/CP al abrir (igual que ADD)
    this.initUbicacionFromEdit();
  }

  private loadCatalogs(): void {
    this.generalService.get('GET_USERS').subscribe(r => this.usuarios.set(r ?? []));
    this.generalService.get('GET_PERIODICIDAD_VISITA').subscribe(r => this.periodicidades.set(r ?? []));
    this.generalService.get('GET_ACTIVIDADES_EMPRESA').subscribe(r => this.actividades.set(r ?? []));
    this.generalService.get('GET_TAMANO_EMPRESA').subscribe(r => this.tamanios.set(r ?? []));
    this.hydrateCodigoPostalCatalogFromCache();
    this.generalService.get('GET_CODIGOS_POSTALES').subscribe(r => {
      this.codigoPostalCatalog.set(r ?? []);
      localStorage.setItem('GET_CODIGOS_POSTALES', JSON.stringify(r ?? []));
      this.syncCodigoPostalDescripcion();
    });
    this.generalService.get('GET_DIAS_VISITA').subscribe(r => this.diasVisita.set(r ?? []));
    this.generalService.get('GET_PARAMETROS_PERIODO').subscribe({
      next: (data: any[]) => {
        (data ?? []).forEach(parameter => {
          if (parameter.codigo === 'PERIODO_TITULO') this.periodoTitulo.set(parameter.valor ?? 'Periodo');
          if (parameter.codigo === 'PERIODO_NUMERO') this.periodoLimite.set(Number(parameter.valor ?? 0));
        });

        if (!this.periodoLimite()) {
          this.periodoLimite.set(12);
        }
      },
      error: () => {
        this.periodoTitulo.set('Periodo');
        this.periodoLimite.set(12);
      }
    });
  }

  periodos(): number[] {
    const limit = this.periodoLimite();
    return limit > 0 ? Array.from({ length: limit }, (_, index) => index + 1) : [];
  }

  openMobileSelect(
    controlName: MobileSelectControlName,
    title: string,
    options: MobileSelectOption[]
  ): void {
    this.mobileSelectControl.set(controlName);
    this.mobileSelectTitle.set(title);
    this.mobileSelectOptions.set(options);
    this.mobileSelectQuery.set('');
    this.mobileSelectOpen.set(true);
  }

  closeMobileSelect(): void {
    this.mobileSelectOpen.set(false);
    this.mobileSelectQuery.set('');
    this.mobileSelectOptions.set([]);
    this.mobileSelectTitle.set('');
    this.mobileSelectControl.set(null);
  }

  updateMobileSelectQuery(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    this.mobileSelectQuery.set(input?.value ?? '');
  }

  applyMobileSelectOption(option: MobileSelectOption): void {
    const controlName = this.mobileSelectControl();
    if (!controlName) {
      return;
    }

    this.editForm.patchValue({ [controlName]: option.value } as any);

    if (controlName === 'fkDepartamento') {
      this.onDepartamentoChange();
    } else if (controlName === 'fkMunicipio') {
      this.onMunicipioChange();
    }

    this.closeMobileSelect();
  }

  isActiveMobileSelectOption(option: MobileSelectOption): boolean {
    const controlName = this.mobileSelectControl();
    if (!controlName) {
      return false;
    }

    return String(this.editForm.get(controlName)?.value ?? '') === String(option.value);
  }

  getDepartamentoMobileOptions(): MobileSelectOption[] {
    return this.departaments.map((department) => ({
      value: department.id,
      label: department.nombre
    }));
  }

  getMunicipioMobileOptions(): MobileSelectOption[] {
    return this.municipalities().map((municipality) => ({
      value: municipality.id,
      label: municipality.nombre
    }));
  }

  getUsuarioMobileOptions(): MobileSelectOption[] {
    return this.usuarios().map((user) => ({
      value: Number(user?.pk ?? 0),
      label: String(user?.nombre ?? user?.descripcion ?? '').trim()
    })).filter((option) => option.value > 0 && option.label);
  }

  getPeriodoMobileOptions(): MobileSelectOption[] {
    return this.periodos().map((period) => ({
      value: period,
      label: `${this.periodoTitulo()} ${period}`
    }));
  }

  getPeriodicidadMobileOptions(): MobileSelectOption[] {
    return this.periodicidades().map((item) => ({
      value: Number(item?.pk ?? 0),
      label: String(item?.descripcion ?? '').trim()
    })).filter((option) => option.value > 0 && option.label);
  }

  getDiaVisitaMobileOptions(): MobileSelectOption[] {
    return this.diasVisita().map((item) => ({
      value: Number(item?.pk ?? 0),
      label: String(item?.descripcion ?? '').trim()
    })).filter((option) => option.value > 0 && option.label);
  }

  getActividadMobileOptions(): MobileSelectOption[] {
    return this.actividades().map((item) => ({
      value: Number(item?.pk ?? 0),
      label: String(item?.descripcion ?? '').trim()
    })).filter((option) => option.value > 0 && option.label);
  }

  getTamanoEmpresaMobileOptions(): MobileSelectOption[] {
    return this.tamanios().map((item) => ({
      value: Number(item?.pk ?? 0),
      label: String(item?.descripcion ?? '').trim()
    })).filter((option) => option.value > 0 && option.label);
  }

  getMobileSelectionLabel(
    controlName: MobileSelectControlName,
    placeholder: string
  ): string {
    const currentValue = this.editForm.get(controlName)?.value;
    const options = this.getMobileSelectionOptions(controlName);

    if (currentValue === null || currentValue === undefined || currentValue === '' || Number(currentValue) === 0) {
      return placeholder;
    }

    const option = options.find((item) => String(item.value) === String(currentValue));
    return option?.label ?? placeholder;
  }

  // ====== FECHAS ======
  private toDatetimeLocal(value: any): string {
    if (!value) return '';
    const s = String(value).trim();

    const m1 = s.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/);
    if (m1) return `${m1[1]}T${m1[2]}`;

    const m2 = s.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);
    if (m2) return m2[1];

    return s;
  }

  private toIsoSeconds(value: any): string {
    if (!value) return '';
    const s = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(s)) return `${s}:00`;
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(s)) return s.replace(' ', 'T') + ':00';
    return s;
  }

  private nowLocalDateTime(): string {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  }

  private resolveEmail(value: string | null | undefined): string {
    const email = String(value ?? '').trim();
    return email || DEFAULT_EMAIL;
  }

  private normalizePhoneText(value: unknown): string {
    return String(value ?? '').trim();
  }

  private normalizeNit(value: unknown): string {
    return String(value ?? '').trim().toUpperCase();
  }

  applyDefaultEmail(): void {
    const control = this.editForm.get('email');
    if (!control) return;
    control.setValue(this.resolveEmail(control.value), { emitEvent: false });
  }

  preventInvalidIntegerInput(event: KeyboardEvent): void {
    if (['e', 'E', '+', '-', '.', ','].includes(event.key)) {
      event.preventDefault();
    }
  }

  normalizeNonNegativeInteger(controlName: 'ordenRuta'): void {
    const control = this.editForm.get(controlName);
    if (!control) return;

    const numericValue = Number(control.value);
    const safeValue = Number.isFinite(numericValue) && numericValue >= 0
      ? Math.trunc(numericValue)
      : 0;

    control.patchValue(safeValue, { emitEvent: false });
  }

  private mapFountainToForm(f: Fountain) {
    return {
      codigoINE: f.codigoINE ?? '',
      nombre: f.nombre ?? '',
      razonSocial: f.razonSocial ?? '',
      direccion: f.direccion ?? '',

      fkDepartamento: Number(f.fkDepartamento ?? 0),
      fkMunicipio: Number(f.fkMunicipio ?? 0),

      zona: Number(f.zona ?? 0),
      fkCodigoPostal: Number(f.fkCodigoPostal ?? 0),

      gpsLatitud: Number(f.gpsLatitud ?? 0),
      gpsLongitud: Number(f.gpsLongitud ?? 0),
      gpsAltitud: Number(f.gpsAltitud ?? 0),
      gpsFecha: this.toDatetimeLocal(f.gpsFecha),

      telefonos: f.telefonos ?? '',
      email: this.resolveEmail(f.email),

      fkUsuario: Number(f.fkUsuario ?? 0),
      periodo: Number(f.periodo ?? 0),
      ordenRuta: Number(f.ordenRuta ?? 0),
      fkPeriodicidadVisita: Number(f.fkPeriodicidadVisita ?? 0),
      fkDiaVisita: Number(f.fkDiaVisita ?? 0),

      horaInicioPrimeraJornada: f.horaInicioPrimeraJornada || DEFAULT_JORNADAS.horaInicioPrimeraJornada,
      horaFinalPrimeraJornada: f.horaFinalPrimeraJornada || DEFAULT_JORNADAS.horaFinalPrimeraJornada,
      horaInicioSegundaJornada: f.horaInicioSegundaJornada || DEFAULT_JORNADAS.horaInicioSegundaJornada,
      horaFinalSegundaJornada: f.horaFinalSegundaJornada || DEFAULT_JORNADAS.horaFinalSegundaJornada,

      requiereAudio: f.requiereAudio ?? 'N',
      requiereImagen: f.requiereImagen ?? 'N',

      nit: f.nit ?? '',
      fkActividadEmpresa: Number(f.fkActividadEmpresa ?? 0),
      fkTamanoEmpresa: Number(f.fkTamanoEmpresa ?? 0),
      codigoCIIU: f.codigoCIIU ?? '',

      comentarios: f.comentarios ?? '',
      referencia: f.referencia ?? '',
      informacionAdicional: f.informacionAdicional ?? '',

      estado: f.estado ?? 'P'
    };
  }

  // =====================================================
  // ✅ LÓGICA IGUAL QUE ADD (DEP/MUNI/ZONA/CP)
  // =====================================================

  private initUbicacionFromEdit(): void {
    const depId = Number(this.editForm.get('fkDepartamento')?.value || 0);
    const munId = Number(this.editForm.get('fkMunicipio')?.value || 0);
    const zona = Number(this.editForm.get('zona')?.value || 0);

    this.municipalities.set([]);
    this.codigosPostales.set([]);

    if (!depId) return;

    this.municipalityService.getMunicipalityByDepartament(depId).subscribe({
      next: (data: any[]) => {
        const validIds = (data ?? [])
          .map(m => Number(m.pk ?? m.id ?? m.municipioId))
          .filter(n => !Number.isNaN(n));

        this.municipalities.set(
          Object.entries(MunicipalityGT)
            .filter(([_, id]) => validIds.includes(Number(id)))
            .map(([name, id]) => ({ id: Number(id), nombre: name }))
        );

        if (munId) {
          this.loadCodigosPostalesImmediately(depId, munId, zona, true);
        }
      },
      error: () => {
        this.municipalities.set([]);
        this.error.set('No se pudieron cargar los municipios.');
      }
    });
  }

  onDepartamentoChange(event?: Event): void {
    const depId = event
      ? Number((event.target as HTMLSelectElement).value)
      : Number(this.editForm.get('fkDepartamento')?.value || 0);

    this.municipalities.set([]);
    this.codigosPostales.set([]);
    this.codigoPostalDescripcion = '';

    this.editForm.patchValue({
      fkMunicipio: 0,
      fkCodigoPostal: 0
    });

    if (!depId) return;

    this.municipalityService.getMunicipalityByDepartament(depId).subscribe({
      next: (data: any[]) => {
        const validIds = (data ?? [])
          .map(m => Number(m.pk ?? m.id ?? m.municipioId))
          .filter(n => !Number.isNaN(n));

        this.municipalities.set(
          Object.entries(MunicipalityGT)
            .filter(([_, id]) => validIds.includes(Number(id)))
            .map(([name, id]) => ({ id: Number(id), nombre: name }))
        );
      },
      error: () => {
        this.municipalities.set([]);
        this.error.set('No se pudieron cargar los municipios.');
      }
    });
  }

  onMunicipioChange(): void {
    const depId = Number(this.editForm.get('fkDepartamento')?.value || 0);
    const munId = Number(this.editForm.get('fkMunicipio')?.value || 0);
    const zona = Number(this.editForm.get('zona')?.value || 0);

    if (!depId || !munId) {
      this.codigosPostales.set([]);
      this.editForm.patchValue({ fkCodigoPostal: 0 });
      this.codigoPostalDescripcion = '';
      return;
    }

    this.fetchCodigosPostales(depId, munId, zona, false);
  }

  onZonaChange(): void {
    this.onMunicipioChange();
  }

  private fetchCodigosPostales(depId: number, munId: number, zona: number, preservarActual: boolean): void {
    const referencia = zona && zona > 0 ? `Zona ${zona}` : '0';
    this.codigoPostalQuery$.next({ depId, munId, referencia, preservarActual });
  }

  private getMobileSelectionOptions(controlName: MobileSelectControlName): MobileSelectOption[] {
    switch (controlName) {
      case 'fkDepartamento':
        return this.getDepartamentoMobileOptions();
      case 'fkMunicipio':
        return this.getMunicipioMobileOptions();
      case 'fkUsuario':
        return this.getUsuarioMobileOptions();
      case 'periodo':
        return this.getPeriodoMobileOptions();
      case 'fkPeriodicidadVisita':
        return this.getPeriodicidadMobileOptions();
      case 'fkDiaVisita':
        return this.getDiaVisitaMobileOptions();
      case 'requiereAudio':
      case 'requiereImagen':
        return this.yesNoOptions;
      case 'fkActividadEmpresa':
        return this.getActividadMobileOptions();
      case 'fkTamanoEmpresa':
        return this.getTamanoEmpresaMobileOptions();
      case 'estado':
        return this.statusOptions;
      default:
        return [];
    }
  }

  private loadCodigosPostalesImmediately(
    depId: number,
    munId: number,
    zona: number,
    preservarActual: boolean
  ): void {
    const referencia = zona && zona > 0 ? `Zona ${zona}` : '0';

    this.requestCodigoPostalLookup(depId, munId, referencia, preservarActual)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => this.applyCodigoPostalLookupResult(result));
  }

  private requestCodigoPostalLookup(
    depId: number,
    munId: number,
    referencia: string,
    preservarActual: boolean
  ): Observable<CodigoPostalLookupResult> {
    return this.codigoPostalService.getCodigosPostales(depId, munId, referencia).pipe(
      switchMap((lista) => {
        if ((lista?.length ?? 0) === 0 && referencia !== '0') {
          return this.codigoPostalService.getCodigosPostales(depId, munId, '0').pipe(
            map((fallbackList): CodigoPostalLookupResult => ({
              lista: fallbackList,
              preservarActual,
              preserveCurrentState: false
            }))
          );
        }

        return of({
          lista,
          preservarActual,
          preserveCurrentState: false
        } as CodigoPostalLookupResult);
      }),
      catchError((error) => {
        if (this.isTransientCodigoPostalError(error)) {
          return of({
            lista: this.codigosPostales(),
            preservarActual: true,
            preserveCurrentState: true
          });
        }

        return of({
          lista: [],
          preservarActual,
          preserveCurrentState: false
        });
      })
    );
  }

  private applyCodigoPostalLookupResult({ lista, preservarActual, preserveCurrentState }: CodigoPostalLookupResult): void {
    if (preserveCurrentState) {
      return;
    }

    this.codigosPostales.set(lista);

    const actual = Number(this.editForm.get('fkCodigoPostal')?.value || 0);
    const descripcionActual = this.resolveCurrentCodigoPostalDescripcion(actual);

    if (!lista.length) {
      if (preservarActual && actual > 0 && descripcionActual) {
        this.codigoPostalDescripcion = descripcionActual;
        return;
      }

      this.editForm.patchValue({ fkCodigoPostal: 0 });
      this.codigoPostalDescripcion = '';
      return;
    }

    const itemActual = lista.find(x => Number(x.pk) === actual);
    if (preservarActual && actual > 0 && !itemActual && descripcionActual) {
      this.codigoPostalDescripcion = descripcionActual;
      return;
    }

    const elegido = (preservarActual && itemActual) ? actual : Number(lista[0].pk);

    this.editForm.patchValue({ fkCodigoPostal: elegido });

    const item = lista.find(x => Number(x.pk) === elegido);
    this.codigoPostalDescripcion = item?.descripcion ?? '';
  }

  private hydrateCodigoPostalCatalogFromCache(): void {
    if (this.codigoPostalCatalog().length > 0) {
      return;
    }

    const cached = localStorage.getItem('GET_CODIGOS_POSTALES');
    if (!cached) {
      return;
    }

    try {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed) && parsed.length > 0) {
        this.codigoPostalCatalog.set(parsed);
      }
    } catch {
      // Ignore malformed local cache and keep the async request as fallback.
    }
  }

  private resolveCodigoPostalDescripcion(fountain: Fountain | null | undefined): string {
    if (!fountain) {
      return '';
    }

    const raw = fountain as any;

    return String(
      raw?.codigoPostalDescripcion ??
      raw?.codigoPostal ??
      raw?.descripcionCodigoPostal ??
      raw?.nombreCodigoPostal ??
      this.resolveCodigoPostalDescripcionFromCatalog(Number(raw?.fkCodigoPostal ?? 0)) ??
      ''
    ).trim();
  }

  private resolveCodigoPostalDescripcionFromCatalog(codigoPostalId: number): string {
    if (!Number.isFinite(codigoPostalId) || codigoPostalId <= 0) {
      return '';
    }

    const item = this.codigoPostalCatalog().find((catalogItem) =>
      Number(catalogItem?.pk ?? catalogItem?.id ?? 0) === codigoPostalId
    );

    return String(item?.descripcion ?? item?.nombre ?? '').trim();
  }

  private resolveCurrentCodigoPostalDescripcion(codigoPostalId: number): string {
    const currentDescription = `${this.codigoPostalDescripcion ?? ''}`.trim();
    if (currentDescription) {
      return currentDescription;
    }

    const selectedOption = this.codigosPostales().find((item) => Number(item?.pk ?? 0) === codigoPostalId);
    const optionDescription = String(selectedOption?.descripcion ?? '').trim();
    if (optionDescription) {
      return optionDescription;
    }

    const sourceDescription = this.resolveCodigoPostalDescripcion(this.fountain);
    if (sourceDescription) {
      return sourceDescription;
    }

    return this.resolveCodigoPostalDescripcionFromCatalog(codigoPostalId);
  }

  private syncCodigoPostalDescripcion(): void {
    const codigoPostalId = Number(this.editForm.get('fkCodigoPostal')?.value || this.fountain?.fkCodigoPostal || 0);
    this.codigoPostalDescripcion = this.resolveCurrentCodigoPostalDescripcion(codigoPostalId);
  }

  private normalizeSearchText(value: unknown): string {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }

  // ====== GEO ======
  useCurrentLocation(): void {
    if (this.geoLoading()) return;

    if (!navigator.geolocation) {
      this.error.set('El navegador no soporta geolocalización.');
      return;
    }

    this.geoLoading.set(true);
    this.error.set(null);

    navigator.geolocation.getCurrentPosition(
      pos => {
        this.editForm.patchValue({
          gpsLatitud: pos.coords.latitude,
          gpsLongitud: pos.coords.longitude,
          gpsAltitud: pos.coords.altitude ?? 0,
          gpsFecha: this.nowLocalDateTime()
        });
        this.geoLoading.set(false);
      },
      () => {
        this.geoLoading.set(false);
        this.error.set('No se pudo obtener la ubicación.');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }

  canOpenGoogleMaps(): boolean {
    return !!this.buildGoogleMapsUrl();
  }

  openGoogleMaps(): void {
    const url = this.buildGoogleMapsUrl();
    if (!url) {
      this.error.set('La fuente no tiene ubicación suficiente para consultarla en Google Maps.');
      return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  }

  openGenerateBoletaModal(): void {
    const sourceId = Number(this.fountain?.pk ?? this.fountain?.id ?? 0);
    if (!sourceId) {
      this.boletaMessageType.set('danger');
      this.boletaMessage.set('No se encontró el identificador de la fuente para generar la boleta.');
      return;
    }

    const segUsuario = getCurrentUserId();

    this.loadingOpenMonths.set(true);
    this.boletaMessage.set(null);
    this.error.set(null);
    this.openMonthsModalOpen.set(false);
    this.openPublicationMonths.set([]);
    this.selectedOpenPublicationMonthKey.set(null);

    this.sourceService.getOpenPublicationMonths(segUsuario).subscribe({
      next: (response) => {
        const months = this.extractOpenPublicationMonths(response);
        this.loadingOpenMonths.set(false);

        if (!months.length) {
          this.boletaMessageType.set('danger');
          this.boletaMessage.set('No hay meses habilitados para generar boletas en este momento.');
          return;
        }

        this.openPublicationMonths.set(months);
        this.selectedOpenPublicationMonthKey.set(this.getOpenPublicationMonthKey(months[0]));
        this.openMonthsModalOpen.set(true);
      },
      error: (error) => {
        this.loadingOpenMonths.set(false);
        this.boletaMessageType.set('danger');
        this.boletaMessage.set(
          this.buildBoletaErrorMessage(error?.error ?? error, 'No fue posible consultar los meses habilitados para generar la boleta.')
        );
      }
    });
  }

  closeOpenMonthsModal(): void {
    this.openMonthsModalOpen.set(false);
    this.openPublicationMonths.set([]);
    this.selectedOpenPublicationMonthKey.set(null);
  }

  selectOpenPublicationMonth(option: OpenPublicationMonthOption): void {
    this.selectedOpenPublicationMonthKey.set(option.key);
  }

  isSelectedOpenPublicationMonth(option: OpenPublicationMonthOption): boolean {
    return this.selectedOpenPublicationMonthKey() === option.key;
  }

  confirmGenerateBoleta(): void {
    const sourceId = Number(this.fountain?.pk ?? this.fountain?.id ?? 0);
    if (!sourceId) {
      this.boletaMessageType.set('danger');
      this.boletaMessage.set('No se encontró el identificador de la fuente para generar la boleta.');
      return;
    }

    const selectedMonth = this.openPublicationMonths().find(
      (month) => this.getOpenPublicationMonthKey(month) === this.selectedOpenPublicationMonthKey()
    );

    if (!selectedMonth) {
      this.boletaMessageType.set('danger');
      this.boletaMessage.set('Seleccione un mes habilitado antes de generar la boleta.');
      return;
    }

    const request: GenerateNewSourceBoletaRequest = {
      pkFuente: sourceId,
      anio: selectedMonth.anio,
      mes: selectedMonth.mes,
      segFuncion: 'CREAR_BOLETA',
      segUsuario: getCurrentUserId(),
      segTransaccion: 3,
      segInfoAdicional: ''
    };

    this.generatingBoleta.set(true);
    this.boletaMessage.set(null);
    this.error.set(null);

    this.sourceService.generateNewSourceBoleta(request).subscribe({
      next: (response) => {
        this.generatingBoleta.set(false);
        this.closeOpenMonthsModal();
        this.boletaMessageType.set(this.isBackendFailureResponse(response) ? 'danger' : 'success');
        this.boletaMessage.set(this.buildBoletaSuccessMessage(response));
      },
      error: (error) => {
        this.generatingBoleta.set(false);
        this.boletaMessageType.set('danger');
        this.boletaMessage.set(
          this.buildBoletaErrorMessage(error?.error ?? error, 'No fue posible generar la boleta nueva para la fuente.')
        );
      }
    });
  }

  // ====== AUDITORÍA ======
  private buildDatosAnteriores(snapshot: any, pk: number): string {
    const gpsFecha = this.toIsoSeconds(snapshot?.gpsFecha);

    return [
      `Pk:${pk}`,
      `Codigo_INE:${snapshot?.codigoINE ?? ''}`,
      `Razon_Social:${snapshot?.razonSocial ?? ''}`,
      `Nombre:${snapshot?.nombre ?? ''}`,
      `Direccion:${snapshot?.direccion ?? ''}`,
      `Fk_Codigo_Postal:${snapshot?.fkCodigoPostal ?? 0}`,
      `Zona:${snapshot?.zona ?? 0}`,
      `Fk_Departamento:${snapshot?.fkDepartamento ?? 0}`,
      `Fk_Municipio:${snapshot?.fkMunicipio ?? 0}`,
      `GPS_Latitud:${snapshot?.gpsLatitud ?? 0}`,
      `GPS_Longitud:${snapshot?.gpsLongitud ?? 0}`,
      `GPS_Altitud:${snapshot?.gpsAltitud ?? 0}`,
      `GPS_Fecha:${gpsFecha}`,
      `Telefonos:${snapshot?.telefonos ?? ''}`,
      `Email:${snapshot?.email ?? ''}`,
      `Fk_Usuario:${snapshot?.fkUsuario ?? 0}`,
      `Periodo:${snapshot?.periodo ?? 0}`,
      `Orden_Ruta:${snapshot?.ordenRuta ?? 0}`,
      `Fk_Periodicidad_Visita:${snapshot?.fkPeriodicidadVisita ?? 0}`,
      `Fk_Dia_Visita:${snapshot?.fkDiaVisita ?? 0}`,
      `Hora_Inicio_Primera_Jornada:${snapshot?.horaInicioPrimeraJornada ?? ''}`,
      `Hora_Final_Primera_Jornada:${snapshot?.horaFinalPrimeraJornada ?? ''}`,
      `Hora_Inicio_Segunda_Jornada:${snapshot?.horaInicioSegundaJornada ?? ''}`,
      `Hora_Final_Segunda_Jornada:${snapshot?.horaFinalSegundaJornada ?? ''}`,
      `Requiere_Audio:${snapshot?.requiereAudio ?? 'N'}`,
      `Requiere_Imagen:${snapshot?.requiereImagen ?? 'N'}`,
      `NIT:${snapshot?.nit ?? ''}`,
      `Fk_Actividad_Empresa:${snapshot?.fkActividadEmpresa ?? 0}`,
      `Fk_Tamano_Empresa:${snapshot?.fkTamanoEmpresa ?? 0}`,
      `Codigo_CIIU:${snapshot?.codigoCIIU ?? ''}`,
      `Comentarios:${snapshot?.comentarios ?? ''}`,
      `Referencia:${snapshot?.referencia ?? ''}`,
      `Informacion_Adicional:${snapshot?.informacionAdicional ?? ''}`,
      `Estado:${snapshot?.estado ?? 'P'}|`
    ].join('|');
  }

  private shouldCascadeDeactivate(previousStatus: unknown, nextStatus: unknown): boolean {
    return String(previousStatus ?? '').trim().toUpperCase() !== 'I'
      && String(nextStatus ?? '').trim().toUpperCase() === 'I';
  }

  private deactivateRelatedContactsAndArticles(sourceId: number): Observable<boolean> {
    const userId = getCurrentUserId();

    return forkJoin({
      contacts: this.sourceContactsService.getBySource(sourceId),
      articles: this.sourceArticlesService.getBySource(sourceId)
    }).pipe(
      switchMap(({ contacts, articles }) => {
        const activeContacts = (contacts ?? []).filter((contact) => this.isActiveContact(contact));
        const activeArticles = (articles.data ?? []).filter((article) => this.isActiveArticle(article));

        const requests = [
          ...activeContacts.map((contact) =>
            this.sourceContactsService.deactivate(Number(contact.id), userId).pipe(
              map(() => true),
              catchError(() => of(false))
            )
          ),
          ...activeArticles.map((article) =>
            this.deactivateArticle(article, userId).pipe(
              map(() => true),
              catchError(() => of(false))
            )
          )
        ];

        return requests.length
          ? forkJoin(requests).pipe(map((results) => results.every(Boolean)))
          : of(true);
      }),
      catchError(() => of(false))
    );
  }

  private deactivateArticle(article: SourceArticle, userId: number): Observable<unknown> {
    return this.sourceArticlesService.changeStatus(article.pk, {
      estado: 'I',
      fkUsuario: userId,
      fkTransaccion: 3
    }).pipe(
      catchError((error: unknown) => {
        if (!(error instanceof HttpErrorResponse) || error.status !== 403) {
          return throwError(() => error);
        }

        return this.sourceArticlesService.update(article.pk, {
          fkFuente: Number(article.fkFuente),
          fkArticulo: Number(article.fkArticulo),
          fkUnidadMedida: Number(article.fkUnidadMedida),
          cantidad: Number(article.cantidad ?? 0),
          precio: Number(article.precio ?? 0),
          comentarios: article.comentarios ?? '',
          estado: 'I',
          fkUsuario: userId,
          fkTransaccion: 3
        });
      })
    );
  }

  private isActiveContact(contact: SourceContact | null | undefined): boolean {
    return Number(contact?.id ?? 0) > 0
      && String(contact?.status ?? '').trim().toUpperCase() !== 'I';
  }

  private isActiveArticle(article: SourceArticle | null | undefined): boolean {
    return Number(article?.pk ?? 0) > 0
      && String(article?.estado ?? '').trim().toUpperCase() !== 'I';
  }

  saveChanges(): void {
    if (this.saving()) return;

    this.success.set(false);
    this.error.set(null);

    if (!this.fountain || this.editForm.invalid || !this.snapshotAntes) {
      this.error.set('Por favor completa los campos requeridos.');
      return;
    }

    if (this.editForm.errors?.['traslapeJornadas']) {
      this.error.set(
        'La segunda jornada no puede iniciar antes de que finalice la primera.'
      );
      return;
    }

    if (this.editForm.errors?.['primeraJornadaInvalida']) {
      this.error.set(
        'La hora final de la primera jornada debe ser mayor a la inicial.'
      );
      return;
    }

    if (this.editForm.errors?.['segundaJornadaInvalida']) {
      this.error.set(
        'La hora final de la segunda jornada debe ser mayor a la inicial.'
      );
      return;
    }


    const pk = Number(this.fountain.pk ?? this.fountain.id ?? 0);
    const datosAnteriores = this.buildDatosAnteriores(this.snapshotAntes, pk);

    const raw: any = this.editForm.getRawValue();

    this.applyDefaultEmail();
    raw.email = this.editForm.get('email')?.value ?? DEFAULT_EMAIL;

    const updated: Fountain = {
      ...this.fountain,
      ...raw,
      pk,

      telefonos: this.normalizePhoneText(raw.telefonos),
      email: this.resolveEmail(raw.email),
      nit: this.normalizeNit(raw.nit),

      fkCodigoPostal: Number(raw.fkCodigoPostal ?? 0),
      zona: Number(raw.zona ?? 0),
      fkDepartamento: Number(raw.fkDepartamento ?? 0),
      fkMunicipio: Number(raw.fkMunicipio ?? 0),
      fkUsuario: Number(raw.fkUsuario ?? 0),
      periodo: Number(raw.periodo ?? 0),
      ordenRuta: Number(raw.ordenRuta ?? 0),
      fkPeriodicidadVisita: Number(raw.fkPeriodicidadVisita ?? 0),
      fkDiaVisita: Number(raw.fkDiaVisita ?? 0),
      fkActividadEmpresa: Number(raw.fkActividadEmpresa ?? 0),
      fkTamanoEmpresa: Number(raw.fkTamanoEmpresa ?? 0),

      gpsFecha: this.toIsoSeconds(raw.gpsFecha || this.nowLocalDateTime()),

      segFuncion: 'EDIT',
      segUsuario: this.fountain.segUsuario ?? 1,
      segTransaccion: this.fountain.segTransaccion ?? 3,
      segDatosAnteriores: datosAnteriores
    };
    const shouldDeactivateRelations = this.shouldCascadeDeactivate(this.fountain.estado, updated.estado);

    this.saving.set(true);

    this.sourceService.edit(updated).pipe(
      switchMap(() =>
        shouldDeactivateRelations
          ? this.deactivateRelatedContactsAndArticles(pk)
          : of(true)
      )
    ).subscribe({
      next: (relatedRecordsDeactivated) => {
        this.saving.set(false);
        this.updated.emit(updated);

        if (!relatedRecordsDeactivated) {
          this.error.set('La fuente se actualizó, pero no fue posible desactivar todos los contactos y artículos relacionados.');
          return;
        }

        this.success.set(true);

        setTimeout(() => this.closeModal('editModal'), 700);
        setTimeout(() => this.success.set(false), 3000);
      },
      error: () => {
        this.saving.set(false);
        this.error.set('Error al actualizar la fuente.');
      }
    });
  }

  // ====== MODALES ======
  openDeleteModal(f: Fountain | null): void {
    if (!f) return;
    this.selectedFountain = f;

    const modalEl = document.getElementById('deleteModal');
    if (!modalEl) return;

    let modal = (window as any).bootstrap.Modal.getInstance(modalEl);
    if (!modal) modal = new (window as any).bootstrap.Modal(modalEl);
    modal.show();
  }

  onDeleted(): void {
    this.closeModal('deleteModal');
    this.closeModal('editModal');
    this.deleted.emit();
    this.selectedFountain = null;
  }

  private closeModal(id: string): void {
    const modalEl = document.getElementById(id);
    if (!modalEl) return;

    let modal = (window as any).bootstrap.Modal.getInstance(modalEl);
    if (!modal) modal = new (window as any).bootstrap.Modal(modalEl);
    modal.hide();
  }

  private buildBoletaSuccessMessage(response: GenerateNewSourceBoletaResponse): string {
    const explicitMessage = this.extractBackendMessage(response);
    const payloadRows = this.extractBackendPayloadRows(response);
    const payloadMessage = this.extractBackendPayloadMessage(payloadRows);
    const total = this.extractBackendTotal(response) ?? payloadRows.length;

    const details = [explicitMessage, payloadMessage]
      .filter((value, index, array): value is string =>
        !!value && array.findIndex((item) => item === value) === index
      );

    if (!details.length) {
      if (total > 0) {
        return `Boleta generada correctamente.\nFilas devueltas por el backend: ${total}.`;
      }

      return 'La ejecución terminó correctamente, pero el backend no devolvió filas.';
    }

    return details.join('\n');
  }

  private buildBoletaErrorMessage(response: unknown, fallback: string): string {
    const explicitMessage = this.extractBackendMessage(response);
    const payloadMessage = this.extractBackendPayloadMessage(this.extractBackendPayloadRows(response));
    const details = [explicitMessage, payloadMessage]
      .filter((value, index, array): value is string =>
        !!value && array.findIndex((item) => item === value) === index
      );

    return details.length ? details.join('\n') : fallback;
  }

  private extractOpenPublicationMonths(response: unknown): OpenPublicationMonth[] {
    const rows = this.extractBackendPayloadRows(response);

    return rows
      .map((row) => ({
        anio: Number(row['anio'] ?? row['Anio'] ?? row['ANIO'] ?? 0),
        mes: Number(row['mes'] ?? row['Mes'] ?? row['MES'] ?? 0)
      }))
      .filter((month) => month.anio > 0 && month.mes > 0);
  }

  private extractBackendPayloadRows(response: unknown): Record<string, unknown>[] {
    if (Array.isArray(response)) {
      return response.filter(
        (row): row is Record<string, unknown> => !!row && typeof row === 'object' && !Array.isArray(row)
      );
    }

    if (!response || typeof response !== 'object') {
      return [];
    }

    const candidate = response as Record<string, unknown>;

    if (Array.isArray(candidate['payload'])) {
      return candidate['payload'].filter(
        (row): row is Record<string, unknown> => !!row && typeof row === 'object' && !Array.isArray(row)
      );
    }

    const nestedData = candidate['data'];
    if (nestedData && typeof nestedData === 'object' && !Array.isArray(nestedData)) {
      const nestedPayload = (nestedData as Record<string, unknown>)['payload'];
      if (Array.isArray(nestedPayload)) {
        return nestedPayload.filter(
          (row): row is Record<string, unknown> => !!row && typeof row === 'object' && !Array.isArray(row)
        );
      }
    }

    return [];
  }

  private extractBackendTotal(response: unknown): number | null {
    if (!response || typeof response !== 'object') {
      return null;
    }

    const candidate = response as Record<string, unknown>;
    const directTotal = Number(candidate['total']);
    if (Number.isFinite(directTotal)) {
      return directTotal;
    }

    const nestedData = candidate['data'];
    if (nestedData && typeof nestedData === 'object' && !Array.isArray(nestedData)) {
      const nestedTotal = Number((nestedData as Record<string, unknown>)['total']);
      if (Number.isFinite(nestedTotal)) {
        return nestedTotal;
      }
    }

    return null;
  }

  private extractBackendMessage(response: unknown): string | null {
    if (!response) {
      return null;
    }

    if (typeof response === 'string') {
      const message = response.trim();
      return message ? message : null;
    }

    if (typeof response !== 'object') {
      return null;
    }

    const candidate = response as Record<string, unknown>;
    const preferredKeys = ['message', 'mensaje', 'error', 'detail', 'details', 'descripcion', 'description', 'title'];

    for (const key of preferredKeys) {
      const value = this.toReadableBackendValue(candidate[key]);
      if (value) {
        return value;
      }
    }

    const nestedError = candidate['error'];
    if (nestedError && typeof nestedError === 'object' && !Array.isArray(nestedError)) {
      const nestedErrorMessage = this.extractBackendMessage(nestedError);
      if (nestedErrorMessage) {
        return nestedErrorMessage;
      }
    }

    const nestedData = candidate['data'];
    if (nestedData && typeof nestedData === 'object' && !Array.isArray(nestedData)) {
      const nestedDataMessage = this.extractBackendMessage(nestedData);
      if (nestedDataMessage) {
        return nestedDataMessage;
      }
    }

    return null;
  }

  private extractBackendPayloadMessage(rows: Record<string, unknown>[]): string | null {
    if (!rows.length) {
      return null;
    }

    const preferredKeys = ['message', 'mensaje', 'error', 'detalle', 'descripcion', 'description', 'resultado', 'observacion', 'observaciones'];

    for (const row of rows) {
      for (const key of preferredKeys) {
        const value = this.toReadableBackendValue(row[key]);
        if (value) {
          return value;
        }
      }
    }

    const firstRow = rows.find((row) =>
      Object.values(row).some((value) => this.toReadableBackendValue(value))
    );

    if (!firstRow) {
      return null;
    }

    const summaryLines = Object.entries(firstRow)
      .map(([key, value]) => {
        const readableValue = this.toReadableBackendValue(value);
        return readableValue ? `${this.formatBackendFieldLabel(key)}: ${readableValue}` : null;
      })
      .filter((line): line is string => !!line)
      .slice(0, 4);

    if (!summaryLines.length) {
      return null;
    }

    return `Respuesta backend:\n${summaryLines.join('\n')}`;
  }

  private isBackendFailureResponse(response: unknown): boolean {
    if (!response || typeof response !== 'object') {
      return false;
    }

    const candidate = response as Record<string, unknown>;

    if (candidate['success'] === false || candidate['ok'] === false) {
      return true;
    }

    const directStatus = String(candidate['status'] ?? candidate['estado'] ?? '').trim().toLowerCase();
    if (['error', 'failed', 'failure', 'fail'].includes(directStatus)) {
      return true;
    }

    const explicitMessage = this.extractBackendMessage(response);
    if (explicitMessage && this.looksLikeBackendError(explicitMessage)) {
      return true;
    }

    const payloadMessage = this.extractBackendPayloadMessage(this.extractBackendPayloadRows(response));
    return !!payloadMessage && this.looksLikeBackendError(payloadMessage);
  }

  private looksLikeBackendError(message: string): boolean {
    const normalized = this.normalizeSearchText(message);
    return ['error', 'fallo', 'failed', 'failure', 'no fue posible', 'no se pudo', 'invalido', 'inválido']
      .some((token) => normalized.includes(this.normalizeSearchText(token)));
  }

  private toReadableBackendValue(value: unknown): string | null {
    if (value == null) {
      return null;
    }

    if (typeof value === 'string') {
      const normalized = value.trim();
      return normalized ? normalized : null;
    }

    if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
      return String(value);
    }

    return null;
  }

  private formatBackendFieldLabel(field: string): string {
    return field
      .replace(/_/g, ' ')
      .replace(/\bpk\b/gi, 'PK')
      .replace(/\bine\b/gi, 'INE')
      .replace(/\b\w/g, (match) => match.toUpperCase());
  }

  private normalizeOpenPublicationMonths(months: OpenPublicationMonth[]): OpenPublicationMonth[] {
    return [...(months ?? [])].sort((first, second) => {
      if (first.anio !== second.anio) {
        return first.anio - second.anio;
      }

      return first.mes - second.mes;
    });
  }

  private getOpenPublicationMonthKey(month: OpenPublicationMonth): string {
    return `${month.anio}-${month.mes}`;
  }

  private getOpenPublicationMonthLabel(month: OpenPublicationMonth): string {
    const monthLabel = MONTH_LABELS[Number(month?.mes ?? 0) - 1] ?? `Mes ${month?.mes ?? ''}`;
    return `${monthLabel} ${month?.anio ?? ''}`.trim();
  }

  private buildGoogleMapsUrl(): string | null {
    const raw = this.editForm.getRawValue();
    const latitude = Number(raw.gpsLatitud ?? this.fountain?.gpsLatitud ?? 0);
    const longitude = Number(raw.gpsLongitud ?? this.fountain?.gpsLongitud ?? 0);

    if (Number.isFinite(latitude) && Number.isFinite(longitude) && (latitude !== 0 || longitude !== 0)) {
      return `https://www.google.com/maps?q=${latitude},${longitude}`;
    }

    const zona = Number(raw.zona ?? this.fountain?.zona ?? 0);
    const departamento = this.getDepartamentoLabel(Number(raw.fkDepartamento ?? this.fountain?.fkDepartamento ?? 0));
    const municipio = this.getMunicipioLabel(Number(raw.fkMunicipio ?? this.fountain?.fkMunicipio ?? 0));
    const query = [
      `${raw.nombre ?? this.fountain?.nombre ?? ''}`.trim(),
      `${raw.direccion ?? this.fountain?.direccion ?? ''}`.trim(),
      zona > 0 ? `Zona ${zona}` : '',
      municipio,
      departamento,
      'Guatemala'
    ]
      .filter((value) => !!`${value}`.trim())
      .join(', ');

    return query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : null;
  }

  private getDepartamentoLabel(id: number): string {
    return Number.isFinite(id) && id > 0 ? (DepartmentsGT[id] ?? '') : '';
  }

  private getMunicipioLabel(id: number): string {
    return Number.isFinite(id) && id > 0 ? (MunicipalityGT[id] ?? '') : '';
  }
}
